import { useEffect, useState } from "react";
import FirestoreService from "../services/firestoreService";
import PlayArrow from "@mui/icons-material/PlayArrow";

export default function SongsOfTheDay() {
  const [groupedSongs, setGroupedSongs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = FirestoreService.fetchAllSongsGroupedByDate(
      (fetchedSongs) => {
        setGroupedSongs(fetchedSongs || []);
        setLoading(false);
      },
      setLoading
    );
    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);
  // Helper to format "time ago"
  function timeAgo(timestamp) {
    if (!timestamp) return "";
    const now = Date.now();
    const diff = now - Number(timestamp);
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return `${seconds} seconds ago`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hours ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} days ago`;
    const weeks = Math.floor(days / 7);
    if (weeks < 4) return `${weeks} weeks ago`;
    const months = Math.floor(days / 30);
    return `${months} months ago`;
  }

  // Format date to "MMM DD, YYYY"
  function formatDate(dateString) {
    // Ensure date is parsed as local date if dateString is in 'YYYY-MM-DD' format
    let date;
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      // Parse as local date
      const [year, month, day] = dateString.split("-");
      date = new Date(Number(year), Number(month) - 1, Number(day));
    } else {
      date = new Date(dateString);
    }
    const options = { year: "numeric", month: "short", day: "numeric" };
    return date.toLocaleDateString(undefined, options);
  }
  // Yes, the date parsing logic in formatDate is a good approach for handling 'YYYY-MM-DD' as a local date.
  // However, for even more robust handling (especially with time zones), you might consider using a library like date-fns or dayjs.
  // For most apps, your approach is fine and clear.

  return (
    <div className="w-full mx-auto max-w-[1400px] min-h-screen p-6 sm:p-8 md:p-20 pb-36 sm:pb-0">
      <h2 className="font-black text-2xl text-white mb-5">History</h2>
      {loading ? (
        // ...loading spinner...
        <div role="status">
          <svg
            aria-hidden="true"
            class="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#1DB954]"
            viewBox="0 0 100 101"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
              fill="currentColor"
            />
            <path
              d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
              fill="currentFill"
            />
          </svg>
          <span class="sr-only">Loading...</span>
        </div>
      ) : (
        <ul className="list-none pb-40 sm:p-0 m-0">
          {groupedSongs.length === 0 ? (
            <li className="text-white">
              Be the first to add your song of the day. None added yet
            </li>
          ) : (
            Object.entries(groupedSongs).map(([date, songs]) => (
              <li key={date}>
                <div className="text-lg font-bold text-green-400 mb-2 mt-6">
                  {formatDate(date)}
                </div>
                <ul className="list-none p-0 m-0">
                  {songs.map((song, idx) => (
                    <li key={song.id || idx}>
                      <div
                        className="flex flex-col md:flex-row items-center rounded-lg mb-4 no-underline transition-colors duration-200 p-3 bg-[#232323] hover:bg-[#333]"
                        style={{ textDecoration: "none" }}
                      >
                        <div className="flex items-center w-full md:w-auto">
                          <span
                            className="w-10 h-10 pr-4 pl-2 md:pl-8 md-mr-4 border-none rounded-full flex items-center justify-center mr-0 sm:mr-4 mb-2 sm:mb-0 cursor-pointer transition-colors duration-200 group"
                            title="Play"
                            tabIndex={-1}
                            onClick={() => {
                              if (song.spotify_link) {
                                window.open(
                                  song.spotify_link,
                                  "_blank",
                                  "noopener,noreferrer"
                                );
                              }
                            }}
                          >
                            <PlayArrow
                              style={{ fontSize: 32 }}
                              className="text-white group-hover:text-green-500 transition-colors duration-200"
                            />
                          </span>
                          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-md overflow-hidden mr-0 sm:mr-4 bg-[#333] flex items-center justify-center">
                            {song.song_album ? (
                              <img
                                src={song.song_album}
                                alt="Album Artwork"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full bg-[#222]" />
                            )}
                          </div>
                          <div className="flex-1 flex flex-col sm:flex-row justify-between items-center sm:items-center min-w-0 w-full">
                            <span className="flex flex-col min-w-0 w-full sm:w-auto text-left md:text-left pl-4 gap-1">
                              <span className="font-bold text-base text-white whitespace-wrap">
                                {song.song_name}
                              </span>
                              <div className="text-[#b3b3b3] text-sm whitespace-wrap">
                                <span>{song.artist_name}</span>
                              </div>
                              <div className="text-[#b3b3b3] text-sm whitespace-wrap">
                                {
                                  <>
                                    added by{" "}
                                    {song.user?.displayName ??
                                      song.display_name ??
                                      song.added_by}{" "}
                                  </>
                                }{" "}
                                {song.added_at && <>{timeAgo(song.added_at)}</>}
                              </div>
                            </span>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
